import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "../lib/api";

const AuthContext = createContext(null);

const API = `${API_BASE}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function formatLoginError(e) {
  // Order: backend `detail` > HTTP status hint > axios message > generic
  const fromDetail = formatApiErrorDetail(e?.response?.data?.detail);
  if (fromDetail) return fromDetail;
  const status = e?.response?.status;
  if (status === 401) return "Invalid email or password";
  if (status === 429) return "Too many login attempts. Try again in 15 minutes.";
  if (status >= 500) return "The portal server hit an error. Wait a moment and try again — if it keeps failing, contact your admin.";
  // Network / CORS / blocked request — likely a connectivity or security software issue
  if (e?.message === "Network Error" || !e?.response) {
    return "Couldn't reach the portal server. This is usually a network or browser security issue. Try (1) refreshing the page, (2) opening the link in a different browser, or (3) temporarily disabling browser security extensions / antivirus web protection.";
  }
  return e?.message || "Something went wrong. Please try again.";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      console.error("[login] failed:", { status: e?.response?.status, detail: e?.response?.data?.detail, message: e?.message });
      return { 
        success: false, 
        error: formatLoginError(e) 
      };
    }
  };

  const register = async (userData) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/register`,
        userData,
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      return { 
        success: false, 
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message || "Registration failed"
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error("Logout error:", e);
    } finally {
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
